import { Sparkles, Zap, AlertTriangle } from "lucide-react";
import { storyElement } from "@/lib/story-element";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface MagicWidgetProps {
  mana?: number | null;
  maxMana?: number | null;
  corruption?: number | null;
  spellSlots?: Record<string, { used: number; max: number }> | null;
  knownSpells?: Array<{ name: string; level: number; school?: string; effect?: string }> | null;
  compact?: boolean;
}

export function MagicWidget({ mana, maxMana, corruption, spellSlots, knownSpells, compact }: MagicWidgetProps) {
  const hasMana = mana != null;
  const hasCorruption = corruption != null;
  const hasSlots = spellSlots && Object.keys(spellSlots).length > 0;
  const hasAny = hasMana || hasCorruption || hasSlots;

  if (!hasAny) {
    return (
      <div className="text-xs text-muted-foreground/50 text-center py-4">
        No magic data yet
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {hasMana && (
          <div className="flex items-center gap-1 text-[10px]">
            <Sparkles className="w-3 h-3 text-blue-400" />
            <span className="text-muted-foreground">{mana}/{maxMana ?? "?"}</span>
          </div>
        )}
        {hasCorruption && (
          <div className="flex items-center gap-1 text-[10px]">
            <AlertTriangle className={cn("w-3 h-3", corruption! > 50 ? "text-destructive" : "text-amber-400")} />
            <span className={cn(corruption! > 50 ? "text-destructive" : "text-muted-foreground")}>{corruption}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div {...storyElement("magic-widget")} className="space-y-3">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
        Powers & Magic
      </div>

      {/* Mana bar */}
      {hasMana && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-blue-400">
              <Sparkles className="w-3.5 h-3.5" />
              <span className="font-medium">Mana</span>
            </div>
            <span className="text-muted-foreground text-[10px]">{mana}/{maxMana ?? "?"}</span>
          </div>
          <Progress value={maxMana ? (mana! / maxMana) * 100 : 50} className="h-2" />
        </div>
      )}

      {/* Corruption bar */}
      {hasCorruption && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <div className={cn("flex items-center gap-1.5", corruption! > 50 ? "text-destructive" : "text-amber-400")}>
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="font-medium">Corruption</span>
            </div>
            <span className={cn("text-[10px]", corruption! > 50 ? "text-destructive" : "text-muted-foreground")}>
              {corruption}/100
            </span>
          </div>
          <Progress value={corruption!} className={cn("h-2", corruption! > 75 && "animate-pulse")} />
        </div>
      )}

      {/* Spell slots */}
      {hasSlots && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
            <Zap className="w-3 h-3" />Spell Slots
          </div>
          <div className="grid grid-cols-3 gap-1">
            {Object.entries(spellSlots!).map(([level, slot]) => (
              <div key={level} className="text-center text-[10px] bg-muted/30 rounded px-1 py-0.5">
                <div className="text-muted-foreground">Lv{level}</div>
                <div className="font-medium text-foreground">{slot.max - slot.used}/{slot.max}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Known spells */}
      {knownSpells && knownSpells.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
            Known Spells
          </div>
          <div className="flex flex-wrap gap-1">
            {knownSpells.map((spell) => (
              <Badge key={spell.name} variant="outline" className="text-[10px]">
                {spell.name} (Lv{spell.level})
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
